import * as PropTypes from 'prop-types';
import React from 'react';

import {Review, REVIEW_SHAPE} from 'notebook/reviews/ReviewCriteriaConstants';
import {MultiSelectionBox, TableData} from 'notebook/tables/TableConstants';
import {ASSAY_RECORD_SHAPE, AssayResult} from 'results/assays/AssayRecordConstants';
import {SCHEMA_FIELD_SHAPE, SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {listToTruncatedReadable} from 'util/NameUtil';
import {Optional} from 'util/Types';

import canSetValidation from './canSetValidation';
import SetValidationButton from './SetValidationButton';
import {validatableInputsFromTableSelection} from './ValidatableInput';
import {ValidatableInput, ValidatableInputType} from './ValidationConstants';

function _getModalWarningMessage(
  validatableInputs: Array<ValidatableInput>,
  hiddenFields: Array<SchemaField>
): string | null {
  // Only show warning if setting validation on full row with hidden fields
  const isFullRowSelected = validatableInputs.some((vi) => vi.type === ValidatableInputType.RESULT);
  if (isFullRowSelected && hiddenFields.length) {
    const hiddenFieldsString = listToTruncatedReadable(
      hiddenFields.map((field) => `"${field.displayName}"`),
      2
    );
    return (
      'Validation will be set on the entire row, ' +
      `including hidden columns for these fields: ${hiddenFieldsString}. `
    );
  }
  return null;
}

function FormulaBoxSetValidationButton(props: Props): JSX.Element {
  const assayResultIdsByRow = props.assayResultsByRow.map((result) => (result ? result.id : null));
  const validatableInputs = props.multiSelectionBox
    ? validatableInputsFromTableSelection(props.table, props.multiSelectionBox, assayResultIdsByRow)
    : [];
  return (
    <SetValidationButton
      disabled={props.isLockedOrProtected || !canSetValidation({review: props.review})}
      modalWarningMessage={_getModalWarningMessage(validatableInputs, props.hiddenFields)}
      validatableInputs={validatableInputs}
    />
  );
}
FormulaBoxSetValidationButton.displayName = 'FormulaBoxSetValidationButton';

interface Props {
  assayResultsByRow: Array<AssayResult | null>;
  hiddenFields: Array<SchemaField>;
  isLockedOrProtected: boolean;
  multiSelectionBox: MultiSelectionBox | null;
  review: Optional<Review>;
  table: TableData;
}
FormulaBoxSetValidationButton.propTypes = {
  assayResultsByRow: PropTypes.arrayOf(ASSAY_RECORD_SHAPE).isRequired,
  hiddenFields: PropTypes.arrayOf(SCHEMA_FIELD_SHAPE.isRequired).isRequired,
  isLockedOrProtected: PropTypes.bool.isRequired,
  multiSelectionBox: PropTypes.object,
  review: REVIEW_SHAPE,
  table: PropTypes.object.isRequired,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
} as any;

export default FormulaBoxSetValidationButton;
